/*
 * @FileName       : report.service.ts
 * @Version        : 1.0
 * @DateOfCreation : 04/17/2020
 * @Author         : $u^^!t @ FFF Enterprises
 * @Dependencies   : N/A
 * @Description    : Report Service to perform all reports data call.
 *
 * Copyright (c) 2020, FFF Enterprises. All rights reserved.
 */

import { InventoryFilter, InventoryGroup, Inventory, TransactionLogFilter } from '../models/reports/Inventory';
import { INVENTORY_MANAGEMENT, REPORTS, getRestockOrderDetailsUrl } from '../constants/api.url';
import { HttpResponse, ResultSet } from '../models/common/result.set';
import Api from '../lib/api';

export class ReportService {
	private static instance: ReportService;
	private constructor() {}

	public static getInstance(): ReportService {
		if (!ReportService.instance) {
			ReportService.instance = new ReportService();
		}
		return ReportService.instance;
	}

	public inventoryHistory = async (params: {
		filter: any[];
		table: string;
	}): Promise<HttpResponse<ResultSet<Inventory>>> => {
		const path =
			params.table === 'lot' ? '/history' : params.table === 'cartridge' ? '/cartridge/history' : '/serial/history';
		return await Api.get(INVENTORY_MANAGEMENT.URL + path, params.filter);
	};

	public inventoryGroupByTenants = async (
		filter: InventoryFilter,
	): Promise<HttpResponse<ResultSet<InventoryGroup>>> => {
		const queryParams = filter.option && filter.option.content_type ? '?type=csv' : '';
		return await Api.post(REPORTS.INVENTORY.GROUP + queryParams, filter);
	};

	/*
	 * endpoints to populate the four tables on the restock-order-detail/{orderId} page
	 */
	public getRestockOrderDetails_igShipmentItems = async (orderId: string): Promise<any> => {
		return await Api.get(getRestockOrderDetailsUrl('IG_SHIPMENT_ITEMS', orderId));
	};

	public getRestockOrderDetails_restockRequest = async (orderId: string): Promise<any> => {
		return await Api.get(getRestockOrderDetailsUrl('RESTOCK_REQUEST', orderId));
	};

	public getRestockOrderDetails_warehouseShipment = async (orderId: string): Promise<any> => {
		return await Api.get(getRestockOrderDetailsUrl('WAREHOUSE_SHIPMENT', orderId));
	};

	public getRestockOrderDetails_transactions = async (orderId: string): Promise<any> => {
		return await Api.get(getRestockOrderDetailsUrl('TRANSACTIONS', orderId));
	};

	public getRestockOrderDetails_sapOrders = async (orderId: string): Promise<any> => {
		return await Api.get(getRestockOrderDetailsUrl('SAP_ORDERS', orderId));
	};

	public getRecentlyVended = async (filter: TransactionLogFilter): Promise<any> => {
		const params = this.getTransactionStringParams(filter);
		let reportResponse = await Api.get(REPORTS.INVENTORY.TRANSACTION, params);
		if (filter.groupBy) {
			const groupKeyMap: any = {
				cabinet: 'cabinetId',
				product: 'productItemId',
				lot: 'lot',
				type: 'type',
				serial_no: 'serial_no',
				rfid_tag: 'rfid_tag'
			};
			const groups: any = {};
			reportResponse.data.result.forEach((group: any) => {
				groups[group[groupKeyMap[filter.groupBy]]] = {};
			});
			reportResponse.data = { groups: groups, groupNames: reportResponse.data.result };
			return reportResponse;
		}
		return reportResponse;
	};

	public requestTransactionLogCsvExport = async (filter: InventoryFilter): Promise<any> => {
		const params = this.getTransactionStringParams(filter);
		const { data } = await Api.get(REPORTS.INVENTORY.TRANSACTION, params);
		var blob = new Blob([data]);
		var link = document.createElement('a');
		link.href = window.URL.createObjectURL(blob);
		link.download = 'report.csv';
		link.click();
	};

	public getDispensedProducts = async (filter: any): Promise<any> => {
		return await Api.get(REPORTS.DISPENSED_PRODUCTS, [{ key: 'filter', value: JSON.stringify(filter) }]);
	};

	public getWarehouseShipments = async (filter: any): Promise<any> => {
		return await Api.get(REPORTS.WAREHOUSE_SHIPMENTS, [{ key: 'filter', value: JSON.stringify(filter) }]);
	};

	// Format key/value pairs for GET request
	private getTransactionStringParams(filter: any) {
		let params = null;

		//correct naming of this param for backend
		//the name 'serial_no' is used in other places, so best to correct only for this specific case
		if (filter && filter['serial_no']) {
			filter['serialNo'] = filter['serial_no'];
			delete filter['serial_no'];
		}

		if (filter) {
			params = Object.keys(filter)
				.map(filterKey => ({
					key: filterKey,
					value: filter[filterKey],
				}))
				.filter(item => item.value); // Get rid of undefined parameter values
		}
		if (params) {
			// Paging and Option are both objects and need to be stringified before sending in GET request
			const pagingIndex = params?.map(e => e.key).indexOf('paging');
			if (pagingIndex && pagingIndex > -1) {
				params[pagingIndex] = { key: 'paging', value: JSON.stringify(params[pagingIndex].value) };
			}

			const optionIndex = params?.map(e => e.key).indexOf('option');
			if (optionIndex && optionIndex > -1)
				params[optionIndex] = { key: 'option', value: JSON.stringify(params[optionIndex].value) };
		}

		return params;
	}
}
